import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { countryCodes, getDefaultCountry, getCountryByName } from "../../data/countryCodes";
import { loginMember } from "../../services/memberApi";
import { saveUserSession, registerUser, getUserSession, isUserLoggedIn, saveLoyaltyDiscount } from "../../utils/auth";
import { fetchRedeemOverrideRules, getAvailablePoints } from "../../services/loyaltyApi";
import { useTranslation } from "../../context/TranslationContext";

const SignInRegisterModal = ({
  show,
  onClose,
  signInTab,
  setSignInTab,
  accommodationGroupDetail,
  accommodationDetail
}) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  // State for Sign In form
  const [signInEmail, setSignInEmail] = useState('');
  const [signInPassword, setSignInPassword] = useState('');
  const [signInErrors, setSignInErrors] = useState({});
  const [isSignInLoading, setIsSignInLoading] = useState(false);
  const [signInSuccess, setSignInSuccess] = useState(false);
  const [showSignInPassword, setShowSignInPassword] = useState(false);

  // State for Register form
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [countryCode, setCountryCode] = useState(getDefaultCountry().code);
  const [mobileNumber, setMobileNumber] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [registerErrors, setRegisterErrors] = useState({});
  const [isRegisterLoading, setIsRegisterLoading] = useState(false);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);

  // State for dropdowns
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');

  // Set default country code based on hotel's country when modal opens
  useEffect(() => {
    if (show) {
      // First try accommodationDetail (direct API response has CountryName)
      const hotelCountry = accommodationDetail?.CountryName ||
                          accommodationDetail?.countryName ||
                          accommodationDetail?.Country ||
                          accommodationDetail?.country ||
                          // Fallback to accommodationGroupDetail
                          accommodationGroupDetail?.CountryName ||
                          accommodationGroupDetail?.countryName ||
                          accommodationGroupDetail?.Country ||
                          accommodationGroupDetail?.country ||
                          accommodationGroupDetail?.accommodationDetails?.[0]?.CountryName ||
                          accommodationGroupDetail?.accommodationDetails?.[0]?.countryName ||
                          accommodationGroupDetail?.accommodationDetails?.[0]?.Country ||
                          accommodationGroupDetail?.accommodationDetails?.[0]?.country ||
                          accommodationGroupDetail?.accommodationdetailsdb?.[0]?.CountryName ||
                          accommodationGroupDetail?.accommodationdetailsdb?.[0]?.countryName ||
                          accommodationGroupDetail?.accommodationdetailsdb?.[0]?.Country ||
                          accommodationGroupDetail?.accommodationdetailsdb?.[0]?.country ||
                          accommodationGroupDetail?.hotelMapping?.CountryName ||
                          accommodationGroupDetail?.hotelMapping?.countryName ||
                          accommodationGroupDetail?.hotelMapping?.Country ||
                          accommodationGroupDetail?.hotelMapping?.country;

      if (hotelCountry) {
        const matchedCountry = getCountryByName(hotelCountry);
        if (matchedCountry) {
          setCountryCode(matchedCountry.code);
        }
      }
    }
  }, [show, accommodationDetail, accommodationGroupDetail]);

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[0-9]{10,15}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  // Sign In validation
  const validateSignIn = () => {
    const errors = {};

    if (!signInEmail.trim()) {
      errors.email = t('auth.emailRequired');
    } else if (!validateEmail(signInEmail)) {
      errors.email = t('auth.invalidEmail');
    }

    if (!signInPassword) {
      errors.password = t('auth.passwordRequired');
    } else if (signInPassword.length < 6) {
      errors.password = t('auth.passwordMinLength');
    }

    setSignInErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Register validation
  const validateRegister = () => {
    const errors = {};

    if (!firstName.trim()) {
      errors.firstName = t('auth.firstNameRequired');
    }

    if (!lastName.trim()) {
      errors.lastName = t('auth.lastNameRequired');
    }

    if (!mobileNumber.trim()) {
      errors.mobileNumber = t('auth.mobileRequired');
    } else if (!validatePhone(mobileNumber)) {
      errors.mobileNumber = t('auth.invalidMobile');
    }

    if (!registerEmail.trim()) {
      errors.email = t('auth.emailRequired');
    } else if (!validateEmail(registerEmail)) {
      errors.email = t('auth.invalidEmail');
    }

    if (!registerPassword) {
      errors.registerPassword = t('auth.passwordRequired');
    } else if (registerPassword.length < 6) {
      errors.registerPassword = t('auth.passwordMinLength');
    }

    // Confirm password validation removed

    setRegisterErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Extract PartnerKey from accommodationGroupDetail
  const getPartnerKey = () => {
    if (accommodationGroupDetail?.accommodationDetails && Array.isArray(accommodationGroupDetail.accommodationDetails) && accommodationGroupDetail.accommodationDetails.length > 0) {
      // Get PartnerKey from first accommodation detail
      return accommodationGroupDetail.accommodationDetails[0]?.PartnerKey || 
             accommodationGroupDetail.accommodationDetails[0]?.partnerKey ||
             accommodationGroupDetail.accommodationDetails[0]?.partner_key ||
             null;
    }
    // Fallback: check if PartnerKey is directly in accommodationGroupDetail
    return accommodationGroupDetail?.PartnerKey || 
           accommodationGroupDetail?.partnerKey ||
           accommodationGroupDetail?.partner_key ||
           null;
  };

  // Extract AccessKey from accommodationGroupDetail
  const getAccessKey = () => {
    // Priority 1: Check accommodationdetailsdb array directly
    if (accommodationGroupDetail?.accommodationdetailsdb && Array.isArray(accommodationGroupDetail.accommodationdetailsdb) && accommodationGroupDetail.accommodationdetailsdb.length > 0) {
      return accommodationGroupDetail.accommodationdetailsdb[0]?.AccessKey || 
             accommodationGroupDetail.accommodationdetailsdb[0]?.accessKey ||
             accommodationGroupDetail.accommodationdetailsdb[0]?.access_key ||
             null;
    }
    
    // Priority 2: Check accommodationDetails array (mapped from accommodationdetailsdb)
    if (accommodationGroupDetail?.accommodationDetails && Array.isArray(accommodationGroupDetail.accommodationDetails) && accommodationGroupDetail.accommodationDetails.length > 0) {
      return accommodationGroupDetail.accommodationDetails[0]?.AccessKey || 
             accommodationGroupDetail.accommodationDetails[0]?.accessKey ||
             accommodationGroupDetail.accommodationDetails[0]?.access_key ||
             null;
    }
    
    // Fallback: check if AccessKey is directly in accommodationGroupDetail
    return accommodationGroupDetail?.AccessKey || 
           accommodationGroupDetail?.accessKey ||
           accommodationGroupDetail?.access_key ||
           null;
  };

  // Get accommodationId from URL or accommodationGroupDetail
  const getAccommodationId = () => {
    // Priority: URL params
    const hotel_id = searchParams.get('hotel_id') || 
                     searchParams.get('accommodationid') || 
                     null;
    
    if (hotel_id) {
      return hotel_id;
    }
    
    // Fallback 1: Check accommodationdetailsdb array directly
    if (accommodationGroupDetail?.accommodationdetailsdb && Array.isArray(accommodationGroupDetail.accommodationdetailsdb) && accommodationGroupDetail.accommodationdetailsdb.length > 0) {
      return accommodationGroupDetail.accommodationdetailsdb[0]?.AccommodationId || 
             accommodationGroupDetail.accommodationdetailsdb[0]?.accommodationId ||
             null;
    }
    
    // Fallback 2: Check accommodationDetails array (mapped from accommodationdetailsdb)
    if (accommodationGroupDetail?.accommodationDetails && Array.isArray(accommodationGroupDetail.accommodationDetails) && accommodationGroupDetail.accommodationDetails.length > 0) {
      return accommodationGroupDetail.accommodationDetails[0]?.AccommodationId || 
             accommodationGroupDetail.accommodationDetails[0]?.accommodationId ||
             null;
    }
    
    return accommodationGroupDetail?.accommodationId || null;
  };

  // Handle Google OAuth login
  const handleGoogleLogin = () => {
    const accommodationId = getAccommodationId();
    const accessKey = getAccessKey();

    if (!accommodationId || !accessKey) {
      setSignInErrors({
        general: t('auth.hotelInfoNotAvailable')
      });
      return;
    }
    
    // Construct Google OAuth URL
    const accKey = `${accommodationId}-${accessKey}`;
    const googleAuthUrl = `https://crm-v2.bookingwhizz.com/auth/google/redirect?accKey=${accKey}`;
    
    // Redirect to Google OAuth
    window.location.href = googleAuthUrl;
  };

 
  // Handle Sign In submit
  const handleSignInSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateSignIn()) {
      return;
    }

    setIsSignInLoading(true);
    setSignInErrors({});
    setSignInSuccess(false);

    try {
      const partnerKey = getPartnerKey();

      const response = await loginMember({
        email: signInEmail.trim(),
        password: signInPassword,
        partnerKey: partnerKey
      });

      // Check for error status codes - don't login if data not found
      const statusCode = response.statusCode || response.status_code || response.StatusCode || '';
      const message = response.message || response.Message || '';

      // If status code indicates data not found, throw error (don't proceed with login)
      if (statusCode === 'INFO_DATA_NOT_FOUND' || statusCode === 'DATA_NOT_FOUND' || 
          (message && (message.toLowerCase().includes('data was not found') || 
           message.toLowerCase().includes('data not found')))) {
        throw new Error(t('auth.noAccountFound'));
      }

      // Check if response indicates failure
      if (response.status === false || (response.success === false && !response.data)) {
        throw new Error(message || t('auth.loginFailed'));
      }

      // Login successful
      setSignInSuccess(true);
      
      // Extract user data and token from response
      const userData = {
        profile: response.profile || response.data?.profile || null,
        data: response.data || null
      };
      
      const token = response.data?.accessToken || response.token || response.data?.token || null;

      // Check if we have valid data to save - must have token and profile/data
      if (!token) {
        throw new Error(t('auth.noTokenReceived'));
      }
      
      if (!userData.profile && !userData.data) {
        throw new Error(t('auth.noUserDataReceived'));
      }

      // Save user session only if we have valid token and user data
      saveUserSession(userData, token);

      // Trigger custom event to notify other components
      window.dispatchEvent(new CustomEvent('userLoggedIn', { detail: { user: userData, token } }));

      // Fetch redeem override rules after successful login
      try {
        // Get hotelId from URL
        const hotelId = searchParams.get('hotel_id');
        
        // Get profileId from user profile
        let profileId = null;
        
        // Check if profile is an array and get masterProfile
        if (userData.profile && Array.isArray(userData.profile) && userData.profile.length > 0) {
          const masterProfile = userData.profile.find(p => p.masterProfile || p.MasterProfile) || userData.profile[0];
          profileId = masterProfile?.CrmProfileId || masterProfile?.crmProfileId || masterProfile?.MasterProfileId || masterProfile?.masterProfileId || masterProfile?.master_profile_id || masterProfile?.id || masterProfile?.ID;
        } 
        // Check if profile is an object
        else if (userData.profile && typeof userData.profile === 'object') {
          profileId = userData.profile.CrmProfileId || userData.profile.crmProfileId || userData.profile.MasterProfileId || userData.profile.masterProfileId || userData.profile.master_profile_id || userData.profile.id || userData.profile.ID;
        }
        
        // Check in data object as well
        if (!profileId && userData.data) {
          if (userData.data.profile && Array.isArray(userData.data.profile) && userData.data.profile.length > 0) {
            const masterProfile = userData.data.profile.find(p => p.masterProfile || p.MasterProfile) || userData.data.profile[0];
            profileId = masterProfile?.CrmProfileId || masterProfile?.crmProfileId || masterProfile?.MasterProfileId || masterProfile?.masterProfileId || masterProfile?.master_profile_id || masterProfile?.id || masterProfile?.ID;
          } else if (userData.data.profile && typeof userData.data.profile === 'object') {
            profileId = userData.data.profile.CrmProfileId || userData.data.profile.crmProfileId || userData.data.profile.MasterProfileId || userData.data.profile.masterProfileId || userData.data.profile.master_profile_id || userData.data.profile.id || userData.data.profile.ID;
          }
          profileId = profileId || userData.data.CrmProfileId || userData.data.crmProfileId || userData.data.MasterProfileId || userData.data.masterProfileId || userData.data.master_profile_id || userData.data.id || userData.data.ID;
        }

        if (hotelId && profileId) {
          const loyaltyResponse = await fetchRedeemOverrideRules({
            accommodationId: hotelId,
            profileId: profileId
          });
          
          // Save loyalty discount data to localStorage
          if (loyaltyResponse && loyaltyResponse.success) {
            saveLoyaltyDiscount(loyaltyResponse);
          }
        } else {
        }

        // Fetch available loyalty points
        let masterProfileId = null;
        if (userData.profile && Array.isArray(userData.profile) && userData.profile.length > 0) {
          const masterProfile = userData.profile.find(p => p.masterProfile || p.MasterProfile) || userData.profile[0];
          masterProfileId = masterProfile?.MasterProfileId || masterProfile?.masterProfileId || masterProfile?.master_profile_id;
        } else if (userData.profile && typeof userData.profile === 'object') {
          masterProfileId = userData.profile.MasterProfileId || userData.profile.masterProfileId || userData.profile.master_profile_id;
        } else if (userData.data) {
          if (userData.data.profile && Array.isArray(userData.data.profile) && userData.data.profile.length > 0) {
            const masterProfile = userData.data.profile.find(p => p.masterProfile || p.MasterProfile) || userData.data.profile[0];
            masterProfileId = masterProfile?.MasterProfileId || masterProfile?.masterProfileId || masterProfile?.master_profile_id;
          } else if (userData.data.profile && typeof userData.data.profile === 'object') {
            masterProfileId = userData.data.profile.MasterProfileId || userData.data.profile.masterProfileId || userData.data.profile.master_profile_id;
          }
          masterProfileId = masterProfileId || userData.data.MasterProfileId || userData.data.masterProfileId || userData.data.master_profile_id;
        }

        if (masterProfileId) {
          try {
            const availablePointsResponse = await getAvailablePoints({
              crmProfileId: masterProfileId,
              categoryId: 2
            });
            
            // Save available points to localStorage for use in UnifiedCheckout
            if (availablePointsResponse) {
              try {
                localStorage.setItem('availableLoyaltyPoints', JSON.stringify(availablePointsResponse));
              } catch (error) {
              }
            }
          } catch (pointsError) {
            // Don't block login if this API call fails
          }
        } else {
        }
      } catch (loyaltyError) {
        // Don't block login if this API call fails
      }

      // Close modal after 1 second
      setTimeout(() => {
        onClose();
        // Reset form
        setSignInEmail('');
        setSignInPassword('');
        setSignInSuccess(false);
      }, 1000);

    } catch (error) {
      setSignInErrors({
        general: error.message || t('auth.loginFailed')
      });
    } finally {
      setIsSignInLoading(false);
    }
  };

  // Handle Register submit
  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateRegister()) {
      return;
    }

    setIsRegisterLoading(true);
    setRegisterErrors({});
    setRegisterSuccess(false);

    try {
      // Get hotel_id from URL params
      const hotel_id = searchParams.get('hotel_id') || 
                       searchParams.get('accommodationid') || 
                       accommodationGroupDetail?.accommodationId || 
                       null;
      
      // Get AccessKey from accommodationGroupDetail
      const AccessKey = getAccessKey();
      
      if (!hotel_id || !AccessKey) {
        throw new Error(t('auth.hotelIdRequired'));
      }

      // Get country code and mobile code from countryCodes
      const selectedCountry = countryCodes.find(c => c.code === countryCode);
      const countrycode = selectedCountry?.code || '';
      const mobilecode = countryCode || selectedCountry?.code || '';

      // Call registerUser from auth.js
      const response = await registerUser({
        firstname: firstName.trim(),
        lastname: lastName.trim(),
        email: registerEmail.trim(),
        password: registerPassword,
        confirmpassword: registerPassword, // Use same password since confirm field is removed
        telephone: mobileNumber.trim(),
        countrycode: countrycode,
        mobilecode: mobilecode,
        hotel_id: hotel_id,
        AccessKey: AccessKey,
        currentUrl: window.location.href
      });

      // Registration successful - Check if verification is required
      
      // Check if verification email was sent
      // Response structure: { success: true, message: "Account verification email sent...", profileId: 19552595, data: {...} }
      const verificationMessage = response.message || response.data?.message || '';
      const isVerificationRequired = response.success && (
        verificationMessage.toLowerCase().includes('verification') || 
        verificationMessage.toLowerCase().includes('verify') ||
        verificationMessage.toLowerCase().includes('email sent')
      );
      
      if (isVerificationRequired) {
        // Verification email sent - show success message
        setRegisterSuccess(true);
        
        // Show verification message to user
        setRegisterErrors({
          verification: verificationMessage || t('auth.verifyEmailMessage')
        });

        // Don't save session yet - user needs to verify email first
        // After verification, user will login normally
        
        // Reset form after showing message (3 seconds delay)
        setTimeout(() => {
          // Switch to Sign In tab so user can login after verification
          setSignInTab('signin');
          // Reset form
          setFirstName('');
          setLastName('');
          setCountryCode(getDefaultCountry().code);
          setMobileNumber('');
          setRegisterPassword('');
          setRegisterSuccess(false);
          // Keep email for pre-filling in sign in form
          const emailToKeep = registerEmail.trim();
          setRegisterEmail('');
          // Pre-fill email in sign in form
          setSignInEmail(emailToKeep);
          // Clear verification message after switching tabs
          setTimeout(() => {
            setRegisterErrors({});
          }, 500);
        }, 3000);
      } else {
        // If no verification required (unlikely but handle it)
        setRegisterSuccess(true);
        
        // Extract user data and token from response
        const userData = {
          profile: response.profile || response.data?.profile || {
            FirstName: firstName.trim(),
            LastName: lastName.trim(),
            Email: registerEmail.trim()
          },
          data: response.data || response || null
        };
        const token = response.data?.accessToken || response.token || response.data?.token || response.accessToken || null;
        
        // Save user session only if token exists
        if (token) {
          saveUserSession(userData, token);
          window.dispatchEvent(new CustomEvent('userLoggedIn', { detail: { user: userData, token } }));
        }

        // Close modal after 1.5 seconds
        setTimeout(() => {
          onClose();
          // Reset form
          setFirstName('');
          setLastName('');
          setCountryCode(getDefaultCountry().code);
          setMobileNumber('');
          setRegisterEmail('');
          setRegisterPassword('');
          setConfirmPassword('');
          setRegisterSuccess(false);
        }, 1500);
      }

    } catch (error) {
      setRegisterErrors({
        general: error.message || t('auth.registrationFailed')
      });
    } finally {
      setIsRegisterLoading(false);
    }
  };

  // Reset errors when switching tabs
  const handleTabChange = (tab) => {
    setSignInTab(tab);
    setSignInErrors({});
    setRegisterErrors({});
  };

  if (!show) return null;
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center backdrop-blur-md bg-black/40 singup-modal overflow-y-auto py-8"
      onClick={onClose} // backdrop click close
      style={{ zIndex: 9999 }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 relative animate-fadeInUp flex flex-col z-[10000]"
        onClick={e => e.stopPropagation()} // stop inside clicks
        style={{ zIndex: 10000 }}
      >
        {/* Tab bar - using theme colors */}
        <div className="flex rounded-t-xl overflow-hidden shadow-lg" style={{ backgroundColor: 'var(--primary-purple, #a0895b)' }}>
          <button
            className={`flex-1 py-3 px-4 text-base font-semibold flex items-center justify-center gap-2 focus:outline-none transition-all duration-200 ${
              signInTab === "signin"
                ? "text-white shadow-inner ring-2"
                : "text-white/80 hover:opacity-80"
            }`}
            style={signInTab === "signin" 
              ? { backgroundColor: 'var(--primary-color, #9B1E34)', ringColor: 'var(--primary-purple, #bba16b)' }
              : { backgroundColor: 'var(--primary-color, #AF2E35)' }
            }
            onClick={() => handleTabChange("signin")}
            role="tab"
            aria-selected={signInTab === "signin"}
          >
            <i className="fa fa-user"></i> {t('auth.signIn')}
          </button>
          <button
            className={`flex-1 py-3 px-4 text-base font-semibold flex items-center justify-center gap-2 focus:outline-none transition-all duration-200 ${
              signInTab === "register"
                ? "text-white shadow-inner ring-2"
                : "text-white/80 hover:opacity-80"
            }`}
            style={signInTab === "register"
              ? { backgroundColor: 'var(--primary-color, #9B1E34)', ringColor: 'var(--primary-color, #9B1E34)' }
              : { backgroundColor: 'var(--primary-color, #AF2E35)' }
            }
            onClick={() => handleTabChange("register")}
            role="tab"
            aria-selected={signInTab === "register"}
          >
            <i className="fa fa-user-plus"></i> {t('auth.register')}
          </button>
        </div>

        {/* Content area */}
        <div className="p-6 flex-1">
          {/* Sign In Panel */}
          {signInTab === "signin" && (
            <form className="space-y-4" autoComplete="off" onSubmit={handleSignInSubmit}>
              <div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={signInEmail}
                  onChange={(e) => {
                    setSignInEmail(e.target.value);
                    if (signInErrors.email) {
                      setSignInErrors({ ...signInErrors, email: '' });
                    }
                  }}
                  className={`block w-full bg-transparent border-0 border-b-2 ${
                    signInErrors.email ? 'border-red-500' : 'border-gray-300'
                  } text-gray-900 text-base py-2.5 px-0 focus:ring-0 focus:outline-none placeholder:text-gray-400 transition-colors`}
                  placeholder={t('auth.emailAddress')}
                />
                {signInErrors.email && (
                  <p className="text-red-500 text-xs mt-1">{signInErrors.email}</p>
                )}
              </div>
              <div className="relative">
                <input
                  type={showSignInPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={signInPassword}
                  onChange={(e) => {
                    setSignInPassword(e.target.value);
                    if (signInErrors.password) {
                      setSignInErrors({ ...signInErrors, password: '' });
                    }
                  }}
                  className={`block w-full bg-transparent border-0 border-b-2 pr-10 ${
                    signInErrors.password ? 'border-red-500' : 'border-gray-300'
                  } text-gray-900 text-base py-2.5 px-0 focus:ring-0 focus:outline-none placeholder:text-gray-400 transition-colors`}
                  placeholder={t('auth.password')}
                />
                <button
                  type="button"
                  onClick={() => setShowSignInPassword(!showSignInPassword)}
                  className="absolute right-0 top-0 p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  tabIndex={-1}
                >
                  {showSignInPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
                {signInErrors.password && (
                  <p className="text-red-500 text-xs mt-1">{signInErrors.password}</p>
                )}
              </div>
              
              {/* General error message */}
              {signInErrors.general && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {signInErrors.general}
                </div>
              )}
              
              {/* Success message */}
              {signInSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                  {t('auth.loginSuccess')}
                </div>
              )}
              
              <div className="flex gap-3 pt-3">
                <button
                  type="submit"
                  disabled={isSignInLoading}
                  className={`flex-1 text-white font-semibold py-2.5 rounded-lg transition-all text-sm shadow-md hover:shadow-lg ${
                    isSignInLoading ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                  style={{ 
                    backgroundColor: 'var(--primary-color, #9B1E34)',
                    '--hover-bg': 'var(--primary-color, #8a1a2d)'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSignInLoading) {
                      e.target.style.backgroundColor = 'var(--primary-color, #8a1a2d)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSignInLoading) {
                      e.target.style.backgroundColor = 'var(--primary-color, #9B1E34)';
                    }
                  }}
                >
                  {isSignInLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('auth.signingIn')}
                    </span>
                  ) : (
                    t('auth.signIn')
                  )}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 bg-white border-2 font-semibold py-2.5 rounded-lg transition-all text-sm shadow-md hover:shadow-lg"
                  style={{
                    borderColor: 'var(--primary-color)',
                    color: 'var(--primary-color)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'var(--primary-color)';
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'white';
                    e.target.style.color = 'var(--primary-color)';
                  }}
                >
                  {t('auth.close')}
                </button>
              </div>

              {/* Divider */}
              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">{t('auth.or')}</span>
                </div>
              </div>

              {/* Google Login Button */}
              <button
                type="button"
                onClick={handleGoogleLogin}
                className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-semibold py-2.5 rounded-lg transition-all text-sm shadow-sm hover:shadow-md"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t('auth.continueWithGoogle')}
              </button>
            </form>
          )}

          {/* Register Panel */}
          {signInTab === "register" && (
            <form className="space-y-4" autoComplete="off" onSubmit={handleRegisterSubmit}>
              {/* First Name and Last Name - Side by Side */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <input
                    type="text"
                    id="regfirst-name"
                    name="first-name"
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      if (registerErrors.firstName) {
                        setRegisterErrors({ ...registerErrors, firstName: '' });
                      }
                    }}
                    className={`block w-full bg-transparent border-0 border-b-2 ${
                      registerErrors.firstName ? 'border-red-500' : 'border-gray-300'
                    } text-gray-900 text-base py-2.5 px-0 focus:ring-0 focus:outline-none placeholder:text-gray-400 transition-colors`}
                    placeholder={t('auth.firstName')}
                  />
                  {registerErrors.firstName && (
                    <p className="text-red-500 text-xs mt-1">{registerErrors.firstName}</p>
                  )}
                </div>
                <div>
                  <input
                    type="text"
                    id="reglast-name"
                    name="last-name"
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value);
                      if (registerErrors.lastName) {
                        setRegisterErrors({ ...registerErrors, lastName: '' });
                      }
                    }}
                    className={`block w-full bg-transparent border-0 border-b-2 ${
                      registerErrors.lastName ? 'border-red-500' : 'border-gray-300'
                    } text-gray-900 text-base py-2.5 px-0 focus:ring-0 focus:outline-none placeholder:text-gray-400 transition-colors`}
                    placeholder={t('auth.lastName')}
                  />
                  {registerErrors.lastName && (
                    <p className="text-red-500 text-xs mt-1">{registerErrors.lastName}</p>
                  )}
                </div>
              </div>

              {/* Mobile Code and Number - Side by Side */}
              <div>
                <div className="grid grid-cols-2 gap-3">
                  {/* Country Code Dropdown */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                      className="w-full flex items-center justify-between bg-transparent border-0 border-b-2 border-gray-300 text-gray-900 text-base py-2.5 px-0 focus:ring-0 focus:outline-none transition-colors cursor-pointer"
                    >
                      <span className="flex items-center gap-2">
                        <span className="text-lg">
                          {countryCodes.find(c => c.code === countryCode)?.flag || '🇮🇳'}
                        </span>
                        <span>{countryCode}</span>
                      </span>
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {/* Dropdown Menu */}
                    {showCountryDropdown && (
                      <>
                        <div 
                          className="fixed inset-0 z-[9998]" 
                          onClick={() => setShowCountryDropdown(false)}
                        ></div>
                        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-[10000] max-h-64 overflow-y-auto w-80">
                          <div className="p-2">
                            <input
                              type="text"
                              placeholder={t('auth.searchCountry')}
                              value={countrySearchTerm}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => setCountrySearchTerm(e.target.value)}
                            />
                            <div className="max-h-56 overflow-y-auto">
                              {countryCodes
                                .filter(country => {
                                  const searchTerm = countrySearchTerm.toLowerCase().trim();
                                  if (!searchTerm) return true;
                                  return country.name.toLowerCase().includes(searchTerm) ||
                                    country.code.includes(searchTerm) ||
                                    country.iso.toLowerCase().includes(searchTerm);
                                })
                                .map((country, index) => (
                                <button
                                  key={`${country.iso}-${index}`}
                                  type="button"
                                  onClick={() => {
                                    setCountryCode(country.code);
                                    setShowCountryDropdown(false);
                                    setCountrySearchTerm('');
                                  }}
                                  className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 transition-colors ${
                                    countryCode === country.code ? 'bg-blue-50' : ''
                                  }`}
                                >
                                  <span className="text-xl">{country.flag}</span>
                                  <span className="flex-1 text-left font-medium">{country.name}</span>
                                  <span className="text-gray-600">{country.code}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  
                  {/* Mobile Number Input */}
                  <input
                    type="tel"
                    value={mobileNumber}
                    onChange={(e) => {
                      setMobileNumber(e.target.value);
                      if (registerErrors.mobileNumber) {
                        setRegisterErrors({ ...registerErrors, mobileNumber: '' });
                      }
                    }}
                    className={`w-full bg-transparent border-0 border-b-2 ${
                      registerErrors.mobileNumber ? 'border-red-500' : 'border-gray-300'
                    } text-gray-900 text-base py-2.5 px-0 focus:ring-0 focus:outline-none placeholder:text-gray-400 transition-colors`}
                    placeholder={t('auth.mobileNumber')}
                  />
                </div>
                {registerErrors.mobileNumber && (
                  <p className="text-red-500 text-xs mt-1">{registerErrors.mobileNumber}</p>
                )}
              </div>

              <div>
                <input
                  type="email"
                  id="regemail"
                  name="email"
                  value={registerEmail}
                  onChange={(e) => {
                    setRegisterEmail(e.target.value);
                    if (registerErrors.registerEmail) {
                      setRegisterErrors({ ...registerErrors, registerEmail: '' });
                    }
                  }}
                  className={`block w-full bg-transparent border-0 border-b-2 ${
                    registerErrors.registerEmail ? 'border-red-500' : 'border-gray-300'
                  } text-gray-900 text-base py-2.5 px-0 focus:ring-0 focus:outline-none placeholder:text-gray-400 transition-colors`}
                  placeholder={t('auth.emailAddress')}
                />
                {registerErrors.registerEmail && (
                  <p className="text-red-500 text-xs mt-1">{registerErrors.registerEmail}</p>
                )}
              </div>
              <div className="relative">
                <input
                  type={showRegisterPassword ? "text" : "password"}
                  id="regpassword"
                  name="regpassword"
                  value={registerPassword}
                  onChange={(e) => {
                    setRegisterPassword(e.target.value);
                    if (registerErrors.registerPassword) {
                      setRegisterErrors({ ...registerErrors, registerPassword: '' });
                    }
                  }}
                  className={`block w-full bg-transparent border-0 border-b-2 pr-10 ${
                    registerErrors.registerPassword ? 'border-red-500' : 'border-gray-300'
                  } text-gray-900 text-base py-2.5 px-0 focus:ring-0 focus:outline-none placeholder:text-gray-400 transition-colors`}
                  placeholder={t('auth.createPassword')}
                />
                <button
                  type="button"
                  onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                  className="absolute right-0 top-0 p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  tabIndex={-1}
                >
                  {showRegisterPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
                {registerErrors.registerPassword && (
                  <p className="text-red-500 text-xs mt-1">{registerErrors.registerPassword}</p>
                )}
              </div>
              
              {/* Verification message */}
              {registerErrors.verification && (
                <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="font-semibold mb-1">{t('auth.verificationEmailSent')}</p>
                      <p>{registerErrors.verification}</p>
                      <p className="mt-2 text-xs">{t('auth.checkEmailVerify')}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* General error message */}
              {registerErrors.general && !registerErrors.verification && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {registerErrors.general}
                </div>
              )}
              
              {/* Success message (only if no verification required) */}
              {registerSuccess && !registerErrors.verification && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                  {t('auth.registrationSuccess')}
                </div>
              )}
              
              <div className="flex gap-3 pt-3">
                <button
                  type="submit"
                  disabled={isRegisterLoading}
                  className={`flex-1 text-white font-semibold py-2.5 rounded-lg transition-all text-sm shadow-md hover:shadow-lg ${
                    isRegisterLoading ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                  style={{ 
                    backgroundColor: 'var(--primary-color, #9B1E34)'
                  }}
                  onMouseEnter={(e) => {
                    if (!isRegisterLoading) {
                      e.target.style.backgroundColor = 'var(--primary-color, #8a1a2d)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isRegisterLoading) {
                      e.target.style.backgroundColor = 'var(--primary-color, #9B1E34)';
                    }
                  }}
                >
                  {isRegisterLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('auth.registering')}
                    </span>
                  ) : (
                    t('auth.register')
                  )}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 bg-white border-2 font-semibold py-2.5 rounded-lg transition-all text-sm shadow-md hover:shadow-lg"
                  style={{
                    borderColor: 'var(--primary-color)',
                    color: 'var(--primary-color)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'var(--primary-color)';
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'white';
                    e.target.style.color = 'var(--primary-color)';
                  }}
                >
                  {t('auth.close')}
                </button>
              </div>

              {/* Divider */}
              <div className="relative ">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">{t('auth.or')}</span>
                </div>
              </div>

              {/* Google Login Button */}
              <button
                type="button"
                onClick={handleGoogleLogin}
                className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-semibold py-2.5 rounded-lg transition-all text-sm shadow-sm hover:shadow-md"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t('auth.continueWithGoogle')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignInRegisterModal;
